import React, { useState, useEffect, useMemo, useRef } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import {
    Box, IconButton, Popover, FormControlLabel, Checkbox,
    Typography, Tooltip, Skeleton, useTheme, useMediaQuery
} from '@mui/material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';

// Skeleton overlay component for loading state
function TableSkeletonOverlay({ columns = [] }) {
    // If no columns provided, fallback to generic
    const colsToRender = columns && columns.length > 0
        ? columns
        : Array.from({ length: 5 }).map((_, i) => ({ flex: i === 0 ? 0.5 : 1 }));

    return (
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header Row Skeleton */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: (theme) => theme.palette.datagrid?.headerBgcolor || (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f4f6f8'),
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                minHeight: 56, // DataGrid default header height
            }}>
                {colsToRender.map((col, colIdx) => (
                    <Box key={col.field || colIdx} sx={{
                        display: 'flex',
                        alignItems: 'center',
                        px: 2,
                        flex: col.flex || 0,
                        width: col.width || col.minWidth || (col.flex ? 'auto' : 100),
                        minWidth: col.minWidth || 'auto',
                    }}>
                        {col.headerName ? (
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.875rem' }}>
                                {col.headerName}
                            </Typography>
                        ) : (
                            <Skeleton variant="text" width="60%" height={28} animation="wave" />
                        )}
                    </Box>
                ))}
            </Box>

            {/* Body Rows Skeleton */}
            {Array.from({ length: 15 }).map((_, rowIdx) => (
                <Box key={rowIdx} sx={{
                    display: 'flex',
                    alignItems: 'center',
                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                    minHeight: 52, // DataGrid default row height
                }}>
                    {colsToRender.map((col, colIdx) => (
                        <Box key={col.field || colIdx} sx={{
                            display: 'flex',
                            alignItems: 'center',
                            px: 2,
                            flex: col.flex || 0,
                            width: col.width || col.minWidth || (col.flex ? 'auto' : 100),
                            minWidth: col.minWidth || 'auto',
                        }}>
                            <Skeleton
                                variant="text"
                                width={colIdx === 0 ? '30%' : (colIdx % 2 === 0 ? '80%' : '50%')}
                                height={24}
                                animation="wave"
                            />
                        </Box>
                    ))}
                </Box>
            ))}
        </Box>
    );
}

// Get saved column visibility from localStorage
function getSavedVisibility(tableId) {
    if (!tableId) return {};
    try {
        const saved = localStorage.getItem(`ww_col_vis_${tableId}`);
        return saved ? JSON.parse(saved) : {};
    } catch {
        return {};
    }
}

// Save column visibility to localStorage
function saveVisibility(tableId, model) {
    if (!tableId) return;
    try {
        localStorage.setItem(`ww_col_vis_${tableId}`, JSON.stringify(model));
    } catch { /* ignore */ }
}

export default function DataTable(props) {
    const navigate = useNavigate();
    const theme = useTheme();
    // Column visibility
       const containerRef = useRef(null);
        const [maxHeight, setMaxHeight] = useState(0);
    const [anchorEl, setAnchorEl] = useState(null);
    const [columnVisibilityModel, setColumnVisibilityModel] = useState(() =>
        getSavedVisibility(props.tableId)
    );

    const handleVisibilityChange = (field) => {
        const newModel = {
            ...columnVisibilityModel,
            [field]: columnVisibilityModel[field] === false ? true : (columnVisibilityModel[field] === undefined ? false : !columnVisibilityModel[field])
        };
        setColumnVisibilityModel(newModel);
        saveVisibility(props.tableId, newModel);
    };


    

    return (
        <Box className=" flex flex-1 flex-col">
            {/* Header Content & Column Visibility Toggle */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 2 }}>
                {props.headerContent && (
                    <Box sx={{ flex: 1, display: 'flex' }}>
                        {props.headerContent}
                    </Box>
                )}
                {props.columns && props.columns.length > 3 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', ml: props.headerContent ? 0 : 'auto' }}>
                        <Tooltip title="Show/Hide Columns">
                            <IconButton
                                size="small"
                                onClick={(e) => setAnchorEl(e.currentTarget)}
                                sx={(theme) => ({
                                    border: `1px solid ${theme.palette.divider}`,
                                    borderRadius: '6px',
                                    px: 1.5,
                                    py: 0.5,
                                    color: theme.palette.text.secondary,
                                    '&:hover': {
                                        backgroundColor: theme.palette.action.hover,
                                        color: theme.palette.text.primary,
                                    },
                                })}
                            >
                                <ViewColumnIcon fontSize="small" sx={{ mr: 0.5 }} />
                                <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                                    Columns
                                </Typography>
                            </IconButton>
                        </Tooltip>
                        <Popover
                            open={Boolean(anchorEl)}
                            anchorEl={anchorEl}
                            onClose={() => setAnchorEl(null)}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            PaperProps={{
                                sx: (theme) => ({
                                    p: 2,
                                    minWidth: 200,
                                    maxHeight: 400,
                                    borderRadius: '8px',
                                    border: `1px solid ${theme.palette.divider}`,
                                    boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                                })
                            }}
                        >
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, fontSize: '0.8rem' }}>
                                Toggle Columns
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                {(props.columns || []).map(col => {
                                    const isVisible = columnVisibilityModel[col.field] !== false;
                                    return (
                                        <FormControlLabel
                                            key={col.field}
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    checked={isVisible}
                                                    onChange={() => handleVisibilityChange(col.field)}
                                                    sx={{ py: 0.3 }}
                                                />
                                            }
                                            label={
                                                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                                    {col.headerName}
                                                </Typography>
                                            }
                                            sx={{ ml: 0 }}
                                        />
                                    );
                                })}
                            </Box>
                        </Popover>
                    </Box>
                ) : null}
            </Box>
            <Box ref={containerRef} className="  flex-1" sx={{ maxHeight: containerRef?.current?.offsetHeight}}>

            <DataGrid
                getRowId={(row) => row._id ?? row.id}
                onRowClick={(params) => {
                    if (props.navigate) {
                        const data = params.row;
                        navigate(`${props.navigate}/${data._id}`, { state: { userId: data._id, data_user: data } });
                    }
                }}
                className="flex-1"
                rows={props.rows}
                rowCount={props.rowCount}
                columns={props.columns}
                loading={props.loading}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={(model) => {
                    setColumnVisibilityModel(model);
                    saveVisibility(props.tableId, model);
                }}
                disableColumnMenu
                disableColumnResize
                hideFooter={!props.paginationModel}
                paginationModel={props.paginationModel}
                onPaginationModelChange={props.onPaginationModelChange}
                paginationMode={props.paginationMode || 'client'}
                pageSizeOptions={[10, 15, 20, 25, 30, 50, 100]}
                disableSelectionOnClick
                checkboxSelection={false}
                selectionModel={[]}
                isRowSelectable={() => false}
                slots={{
                    noRowsOverlay: () => (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', py: 6 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                                {props.emptyMessage || 'No data found'}
                            </Typography>
                            {props.emptySubMessage && (
                                <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5 }}>
                                    {props.emptySubMessage}
                                </Typography>
                            )}
                        </Box>
                    ),
                }}
                sx={(theme) => ({
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: '8px',
                    boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
                    minHeight: 200,
                    '& .MuiDataGrid-cell:focus-within': {
                        outline: 'none !important',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                        backgroundColor: theme.palette.datagrid.headerBgcolor,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                    },
                    '& .MuiDataGrid-virtualScroller': {
                        backgroundColor: theme.palette.background.paper,
                        overflowX: 'auto',
                    },
                    '& .MuiDataGrid-footerContainer': {
                        borderTop: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.background.paper,
                    },
                    '& .super-app-theme--header': {
                        backgroundColor: theme.palette.datagrid.headerBgcolor,
                        color: theme.palette.datagrid.headerColor,
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                    },
                    '.MuiDataGrid-columnSeparator': {
                        display: 'none',
                    },
                    '& .MuiDataGrid-columnHeader--sortable': {
                        cursor: 'default !important',
                    },
                    '& .MuiDataGrid-row': {
                        cursor: props.navigate ? 'pointer' : 'default',
                        transition: 'background-color 0.15s ease',
                        '&:hover': {
                            backgroundColor: theme.palette.mode === 'dark'
                                ? 'rgba(255,255,255,0.04)'
                                : 'rgba(0,0,0,0.02)',
                        },
                    },
                    '& .MuiDataGrid-cell': {
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    },
                    '& ::-webkit-scrollbar': {
                        width: '6px',
                        height: '6px',
                    },
                    '& ::-webkit-scrollbar-track': {
                        backgroundColor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#f5f5f5',
                    },
                    '& ::-webkit-scrollbar-thumb': {
                        borderRadius: '10px',
                        backgroundColor: theme.palette.mode === 'dark' ? '#444' : '#ccc',
                        '&:hover': {
                            backgroundColor: theme.palette.mode === 'dark' ? '#666' : '#999',
                        },
                    },
                    // Responsive: allow horizontal scroll on small screens
                    [theme.breakpoints.down('md')]: {
                        '& .MuiDataGrid-main': {
                            overflow: 'auto',
                        },
                    },
                })}
            />
            </Box>
        </Box>
    );
}
